import React, { useState, useEffect } from 'react';

import { useForm, Controller } from 'react-hook-form';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { Input } from '@/components/ui/inputs/input';
import { InputPassWord } from '@/components/ui/inputs/input-password';

import { customerLoginAuthService } from '@/services/AuthServices';
import validationErrors from '@/lib/validationErrors';

import backgroundImage from '@/assets/images/image2.jpg';
import logoImage from '@/assets/images/logo.png';

const FORM_FONT_COLOR = 'rgba(255, 193, 7, 1)';
const BRAND = {
  primary: '#156372', // teal (card)
  yellow: '#FFC107', // page bg + button
  ink: '#0F2F35', // dark text on yellow
};

// 🔥 same yellow input style
const YELLOW_INPUT_CLASS =
  'text-[rgba(255,193,7,1)] placeholder:text-[rgba(255,193,7,0.75)] caret-[rgba(255,193,7,1)]';

const CustomerLoginPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const fromPath = location.state?.from;

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({ defaultValues: { email: '', password: '' } });

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {}, [fromPath]);

  const onSubmit = async ({ email, password }) => {
    setIsLoading(true);
    try {
      const res = await dispatch(
        customerLoginAuthService({ email, password })
      );

      const jwt = res?.data?.data?.token;
      if (jwt) {
        localStorage.removeItem('adminToken');
        localStorage.setItem('customer_jwt', jwt);
        localStorage.setItem('customerToken', jwt);
      }

      reset();
      navigate(
        fromPath && fromPath.startsWith('/menu/table/')
          ? fromPath
          : '/customer/dashboard'
      );
    } catch (err) {
      const { message, errorData } = err || {};
      validationErrors(errorData, message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section
      className="min-h-screen flex items-center"
      style={{ backgroundColor: BRAND.yellow }}
    >
      <div className="mx-auto max-w-7xl px-4 py-10 lg:py-16 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center">
          {/* LEFT — Image panel */}
          <div className="lg:col-span-7 order-1">
            <div
              className="relative h-[44vh] sm:h-[50vh] md:h-[58vh] lg:h-[78vh] rounded-3xl overflow-hidden shadow-2xl ring-1 ring-black/5"
              style={{
                clipPath: 'polygon(0 0, 93% 0, 100% 100%, 0% 100%)',
              }}
            >
              <img
                src={backgroundImage}
                alt="Delicious options on mobile"
                className="absolute inset-0 h-full w-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/15 via-transparent to-transparent" />
              <div
                className="absolute top-0 left-0 h-1 w-1/2"
                style={{ backgroundColor: BRAND.primary }}
              />
              <div className="hidden md:flex absolute bottom-4 left-4">
                <span className="px-4 py-2 rounded-full text-xs font-semibold shadow-md ring-1 ring-black/5 bg-white/90 text-gray-700 backdrop-blur">
                  Earn points on every order
                </span>
              </div>
            </div>
          </div>

          {/* RIGHT — Login card */}
          <div className="lg:col-span-5 order-2">
            <div className="relative lg:-ml-16">
              <div
                className="relative rounded-3xl shadow-2xl ring-1 ring-black/5 overflow-hidden"
                style={{ backgroundColor: BRAND.primary }}
              >
                <div
                  className="w-full h-1"
                  style={{ backgroundColor: '#ffffff22' }}
                />
                <div className="p-6 md:p-10">
                  <div className="flex flex-col items-center mb-6">
                    <img
                      src={logoImage}
                      alt="Company logo"
                      className="h-14 w-auto mb-3"
                    />
                    <h1 className="text-3xl md:text-4xl font-semibold text-center tracking-tight text-white">
                      Customer Login
                    </h1>
                    <p className="mt-2 text-sm text-white/80 text-center">
                      Access orders, rewards, and saved addresses
                    </p>
                  </div>

                  {Object.keys(errors || {}).length > 0 && (
                    <div className="mb-3 rounded-md bg-red-50 text-red-700 text-sm px-3 py-2">
                      Please fix the highlighted fields below.
                    </div>
                  )}

                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    {/* EMAIL */}
                    <Controller
                      name="email"
                      control={control}
                      rules={{
                        required: true,
                        pattern: /^[^@ ]+@[^@ ]+\.[^@ .]{2,}$/,
                      }}
                      render={({ field }) => (
                        <Input
                          {...field}
                          autoFocus
                          type="email"
                          labelText="Email"
                          groupClassName="mb-1"
                          errorType={errors?.email?.type}
                          autoComplete="email"
                          placeholder="Enter your email"
                          // 🔥 yellow text + placeholder
                          inputClassName={YELLOW_INPUT_CLASS}
                        />
                      )}
                    />

                    {/* PASSWORD */}
                    <Controller
                      name="password"
                      control={control}
                      rules={{ required: true, minLength: 6, maxLength: 20 }}
                      render={({ field }) => (
                        <InputPassWord
                          {...field}
                          type="password"
                          labelText="Password"
                          groupClassName="mb-1"
                          errorData={{ minLength: '6', maxLength: '20' }}
                          errorType={errors?.password?.type}
                          autoComplete="current-password"
                          placeholder="Enter your password"
                          // 🔥 yellow text + placeholder
                          inputClassName={YELLOW_INPUT_CLASS}
                        />
                      )}
                    />

                    <div className="flex items-center justify-between">
                      <label
                        htmlFor="remember"
                        className="inline-flex items-center gap-2 select-none text-sm"
                      >
                        <input
                          id="remember"
                          type="checkbox"
                          className="h-4 w-4 rounded"
                          style={{ accentColor: '#FFFFFF' }}
                        />
                        <span className="text-white/90">Remember Me</span>
                      </label>
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full py-3 rounded-xl font-semibold shadow focus:outline-none focus:ring-2 hover:brightness-95 active:brightness-90 disabled:opacity-80 disabled:cursor-not-allowed"
                      style={{
                        backgroundColor: BRAND.yellow,
                        color: BRAND.ink,
                        boxShadow: '0 12px 30px rgba(0,0,0,.25)',
                      }}
                    >
                      {isLoading ? 'Logging in…' : 'Log In'}
                    </button>

                    <div className="text-center pt-2">
                      {fromPath ? (
                        <button
                          type="button"
                          onClick={() => navigate(fromPath)}
                          className="text-sm font-medium hover:underline"
                          style={{ color: BRAND.yellow }}
                        >
                          Go to Previous Page
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => navigate('/sign-in')}
                          className="text-sm font-medium hover:underline"
                          style={{ color: BRAND.yellow }}
                        >
                          Redirect to Admin Login
                        </button>
                      )}
                    </div>

                    <div className="pt-3 text-center text-[11px] text-white/80">
                      Secure login • TLS protected
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CustomerLoginPage;
